/**
 * ChatListService — favourite / archive (and custom) chat lists synced with the server.
 *
 * Sync model (aligned with the mobile app):
 *  - First time (no local copy yet for this user): if the server's system list is empty, push
 *    the user's current local favourites/archives up; otherwise adopt the server's lists.
 *  - Pull (on a chat_list_update presence, on return-from-background, and on reconnect): compare
 *    each pulled list's updated_at against the locally stored one — PER LIST. If the pulled list
 *    is newer, replace the local list with it; otherwise ignore it.
 *  - Push (user favourites/archives): update the local copy and send it to the server, which
 *    bumps updated_at and drives every other device's comparison.
 *
 * The local copy is persisted to localStorage so the per-list updated_at survives reloads and is
 * used for the comparison above. Membership is projected onto each Chat's isFavourite / isArchived
 * flag, so the existing chat-list UI keeps working unchanged.
 *
 * Gated behind config WEBCLIENT.GENERAL.IS_ENABLE_SERVER_CHAT_LIST. When off — or before the first
 * successful pull on a server that lacks /chat_list/* — favourite/archive fall back to the legacy
 * localStorage behaviour in ChatService.
 *
 * See dev_spec/chat-list (server ticket VTGT-1761).
 */

import { Injectable, Injector } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

import * as _ from 'lodash';

import { ChatList } from '../../../../models/chat-list';
import { TeamnoteApiService } from '../../../../api/teamnote-api.service';
import { TeamnoteConfigService } from '../../../../configs/teamnote-config.service';
import { LocalStorageManagerService } from '../../../../utilities/local-storage/local-storage-manager.service';
import { LoggerService } from '../../../../utilities/logger/logger.service';
import { TnNotificationService } from '../../../../utilities/tn-notification/tn-notification.service';
import { AccountManagerService } from '../../account/account-manager.service';
import { TeamNoteApiConstant } from '../../../../constants/api.constant';
import { TeamNoteLocalStorageKeyConstants } from '../../../../constants/local-storage-key.constant';
import { ChatListConstant } from '../../../../constants/chat-list.constant';
import { ChatService } from '../chat/chat.service';

interface ChatLists {
  [listId: string]: ChatList;
}

@Injectable()
export class ChatListService {
  // The local working copy of the lists (mirrors the persisted CHAT_LISTS store).
  lists: ChatLists = {};
  lists$: BehaviorSubject<ChatList[]> = new BehaviorSubject<ChatList[]>([]);

  private _favouriteListId: string = null;
  private _archiveListId: string = null;

  // chat_id -> { list_id: true } membership index, for O(1) projection onto chats
  private _membership: { [chatId: string]: { [listId: string]: boolean } } = {};

  private _reloadTimer: any = null;

  constructor(
    private _teamnoteApiService: TeamnoteApiService,
    private _teamnoteConfigService: TeamnoteConfigService,
    private _localStorageManagerService: LocalStorageManagerService,
    private _loggerService: LoggerService,
    private _tnNotificationService: TnNotificationService,
    private _accountManagerService: AccountManagerService,
    private injector: Injector,
  ) {
    // Pull triggers: return-from-background and network-regain (reconnect is also covered by the
    // roster path in DataManagerService.postGetRosterChecking, and server changes by presence t=331).
    if (typeof document !== 'undefined' && document.addEventListener) {
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
          this.scheduleReload('foreground');
        }
      });
    }
    if (typeof window !== 'undefined' && window.addEventListener) {
      window.addEventListener('online', () => this.scheduleReload('online'));
    }
  }

  /** Whether the server-synced chat list feature is switched on by config. */
  get isEnabled(): boolean {
    const config = this._teamnoteConfigService.config;
    return !!(config && config.WEBCLIENT && config.WEBCLIENT.GENERAL
      && config.WEBCLIENT.GENERAL.IS_ENABLE_SERVER_CHAT_LIST);
  }

  /** Whether we have resolved the system lists (from a hydrate or a pull) and are driving the flags. */
  hasData(): boolean {
    return this._favouriteListId != null || this._archiveListId != null;
  }

  /** Feature is on AND we have a local copy to drive favourite/archive from. */
  isActive(): boolean {
    return this.isEnabled && this.hasData();
  }

  /** Reset in-memory state and re-hydrate this user's local copy (called on fresh login / roster reset). */
  reset(): void {
    this._loggerService.debug('Resetting chat lists');
    this.lists = {};
    this._favouriteListId = null;
    this._archiveListId = null;
    this._membership = {};
    if (this._reloadTimer) {
      clearTimeout(this._reloadTimer);
      this._reloadTimer = null;
    }
    this.hydrateFromLocal();
    this.updateListsSubject();
  }

  updateListsSubject(): void {
    this.lists$.next(_.toArray(this.lists));
  }

  // ----- Pull -----

  /**
   * Pull the lists from the server and merge them into the local copy per-list (newer wins), then
   * run the one-time first-load seeding. No-op when the feature is disabled.
   */
  load(callback?: Function): void {
    if (!this.isEnabled) {
      return;
    }
    this._teamnoteApiService.callApi(
      TeamNoteApiConstant.CHAT_LIST.LIST,
      {},
      (resp) => {
        this.applyPulledLists(resp);
        this.seedServerFromLegacyLocalIfNeeded(() => {
          if (callback) { callback(); }
        });
      },
      (err) => {
        // Server lacks /chat_list/* or is unreachable — keep whatever local copy we have (or the
        // legacy localStorage fallback in ChatService if we have none). Nothing else to do.
        this._loggerService.error('Chat list pull failed. status=' + (err && err.status));
        if (callback) { callback(); }
      },
      false,  // needs access_token
      false,  // JSON response
      true    // background task (no blocking spinner)
    );
  }

  /**
   * Merge pulled lists into the local copy PER LIST: replace a local list only when the pulled one
   * carries a newer updated_at; otherwise keep the local one.
   */
  private applyPulledLists(pulled: ChatList[]): void {
    _.each(pulled || [], (pl) => {
      if (!pl || !pl.list_id) {
        return;
      }
      const local = this.lists[pl.list_id];
      if (!local || this.parseUpdatedAt(pl.updated_at) > this.parseUpdatedAt(local.updated_at)) {
        this.lists[pl.list_id] = pl;
      }
      // else: local is same-or-newer — ignore the pulled copy for this list
    });
    this.resolveSystemListIds();
    this.rebuildMembershipIndex();
    this.persistLocal();
    this.projectMembershipOntoChats();
    this.updateListsSubject();
  }

  // ----- Membership index / projection -----

  private resolveSystemListIds(): void {
    this._favouriteListId = null;
    this._archiveListId = null;
    _.each(this.lists, (list) => {
      if (list.list_type === ChatListConstant.LIST_TYPE.FAVOURITE) {
        this._favouriteListId = list.list_id;
      } else if (list.list_type === ChatListConstant.LIST_TYPE.ARCHIVE) {
        this._archiveListId = list.list_id;
      }
    });
  }

  private rebuildMembershipIndex(): void {
    this._membership = {};
    _.each(this.lists, (list) => {
      _.each(list.chats, (item) => {
        if (!item || !item.chat_id) {
          return;
        }
        if (!this._membership[item.chat_id]) {
          this._membership[item.chat_id] = {};
        }
        this._membership[item.chat_id][list.list_id] = true;
      });
    });
  }

  isChatInFavourite(chatId: string): boolean {
    return this.isChatInList(chatId, this._favouriteListId);
  }

  isChatInArchive(chatId: string): boolean {
    return this.isChatInList(chatId, this._archiveListId);
  }

  private isChatInList(chatId: string, listId: string): boolean {
    if (!listId || !this._membership[chatId]) {
      return false;
    }
    return !!this._membership[chatId][listId];
  }

  /** Push current favourite/archive membership onto every loaded chat and refresh the chat list. */
  projectMembershipOntoChats(): void {
    const chatService = this.injector.get<ChatService>(ChatService);
    _.each(chatService.chats, (chat) => {
      chat.isFavourite = this.isChatInFavourite(chat.chat_id);
      chat.isArchived = this.isChatInArchive(chat.chat_id);
    });
    chatService.updateChatSubject();
  }

  // ----- Push (optimistic local write-through, with rollback) -----

  setChatFavourite(chatId: string, isFavourite: boolean): void {
    this.mutateMembership(this._favouriteListId, chatId, isFavourite, 'isFavourite');
  }

  setChatArchived(chatId: string, isArchived: boolean): void {
    this.mutateMembership(this._archiveListId, chatId, isArchived, 'isArchived');
  }

  private mutateMembership(listId: string, chatId: string, shouldBeMember: boolean, flagField: string): void {
    if (!listId) {
      this._loggerService.error('Cannot update chat list membership: system list not loaded yet.');
      this._tnNotificationService.showSystemError();
      return;
    }
    // No-op when membership already matches. Guards against spurious HTTP calls from callers
    // that fire unconditionally (e.g. auto-unarchive on every incoming message).
    if (this.isChatInList(chatId, listId) === shouldBeMember) {
      return;
    }
    const chatService = this.injector.get<ChatService>(ChatService);
    const chat = chatService.getChatByChatId(chatId);

    // Optimistic write-through: update the local copy + flag immediately for a snappy UI.
    // (updated_at is left as-is; the authoritative value comes back from the server below.)
    this.applyMembershipLocally(listId, chatId, shouldBeMember);
    if (chat) { chat[flagField] = shouldBeMember; }
    this.persistLocal();
    chatService.updateChatSubject();

    const url = shouldBeMember
      ? TeamNoteApiConstant.CHAT_LIST.ADD_CHAT
      : TeamNoteApiConstant.CHAT_LIST.REMOVE_CHAT;
    const params = {
      list_id: listId,
      chat_ids: JSON.stringify([chatId])  // array params must be JSON-encoded strings (spec §4)
    };

    this._teamnoteApiService.callApi(
      url,
      params,
      (resp) => {
        // Server returns the authoritative updated list (with a bumped updated_at) — adopt it.
        this.applyUpdatedList(resp);
      },
      (err) => {
        this._loggerService.error('Failed to update chat list membership, rolling back. status='
          + (err && err.status));
        this.applyMembershipLocally(listId, chatId, !shouldBeMember);
        if (chat) { chat[flagField] = !shouldBeMember; }
        this.persistLocal();
        chatService.updateChatSubject();
        this._tnNotificationService.showSystemError();
      },
      false,
      false,
      true
    );
  }

  private applyMembershipLocally(listId: string, chatId: string, isMember: boolean): void {
    // membership index
    if (isMember) {
      if (!this._membership[chatId]) {
        this._membership[chatId] = {};
      }
      this._membership[chatId][listId] = true;
    } else if (this._membership[chatId]) {
      delete this._membership[chatId][listId];
    }
    // the local list's chats array (so the persisted copy stays consistent)
    const list = this.lists[listId];
    if (!list) {
      return;
    }
    if (!list.chats) {
      list.chats = [];
    }
    const idx = _.findIndex(list.chats, (c) => c.chat_id === chatId);
    if (isMember && idx === -1) {
      list.chats.push({ chat_id: chatId, sort_order: null });
    } else if (!isMember && idx !== -1) {
      list.chats.splice(idx, 1);
    }
  }

  private applyUpdatedList(list: ChatList): void {
    if (!list || !list.list_id) {
      return;
    }
    this.lists[list.list_id] = list;
    this.resolveSystemListIds();
    this.rebuildMembershipIndex();
    this.persistLocal();
    this.projectMembershipOntoChats();
    this.updateListsSubject();
  }

  // ----- Live sync (presence t = 331) -----

  /**
   * Handle a chat_list_update presence. It carries no list contents, so we debounced-re-pull.
   * Our own edits already returned the updated list in their HTTP response, so a pulled list that
   * is not newer than our local copy is ignored by applyPulledLists (dedup by updated_at, per list).
   */
  onChatListUpdatePresence(presence: any): void {
    if (!this.isEnabled) {
      return;
    }
    // Cheap early-out: if we already hold this list at an equal-or-newer updated_at, skip the pull.
    if (presence && presence.list_id && presence.updated_at != null) {
      const local = this.lists[presence.list_id];
      if (local && this.parseUpdatedAt(presence.updated_at) <= this.parseUpdatedAt(local.updated_at)) {
        this._loggerService.debug('Ignoring already-applied chat_list_update presence for list ' + presence.list_id);
        return;
      }
    }
    this.scheduleReload('presence');
  }

  /**
   * Debounced pull. All pull triggers (roster completion, presence, return-from-background,
   * online) funnel through here so a burst — notably the several GET_ROSTER frames on login —
   * collapses into a single /chat_list/list call after things settle.
   */
  scheduleReload(reason: string): void {
    if (!this.isEnabled || !this._accountManagerService.userId) {
      return;
    }
    this._loggerService.debug('Scheduling chat list pull (' + reason + ')');
    if (this._reloadTimer) {
      clearTimeout(this._reloadTimer);
    }
    this._reloadTimer = setTimeout(() => {
      this._reloadTimer = null;
      this.load();
    }, ChatListConstant.PRESENCE_RELOAD_DEBOUNCE_MS);
  }

  // ----- First-time seeding of legacy local favourites/archives -----

  /**
   * First time only (once per user): if a server system list is empty but the user has legacy
   * localStorage favourites/archives, push them up so the server (and other devices) pick them up.
   * If the server already has lists, they were adopted by applyPulledLists and nothing is pushed.
   */
  private seedServerFromLegacyLocalIfNeeded(done: Function): void {
    const userId = this._accountManagerService.userId;
    const seededUserId = this._localStorageManagerService.getLocalStorageByKey(
      ChatListConstant.LOCAL_STORAGE.MIGRATED_USER_ID
    );
    if (!userId || seededUserId === userId) {
      done();
      return;
    }

    const legacyFavourites = this.readLegacyChatIds(TeamNoteLocalStorageKeyConstants.USER_CONFIG_COOKIES.FAVOURITE_CHAT);
    const legacyArchives = this.readLegacyChatIds(TeamNoteLocalStorageKeyConstants.USER_CONFIG_COOKIES.ARCHIVED_CHAT);

    const pushes: { listId: string, chatIds: string[] }[] = [];
    if (this._favouriteListId && this.isListEmpty(this._favouriteListId) && legacyFavourites.length) {
      pushes.push({ listId: this._favouriteListId, chatIds: legacyFavourites });
    }
    if (this._archiveListId && this.isListEmpty(this._archiveListId) && legacyArchives.length) {
      pushes.push({ listId: this._archiveListId, chatIds: legacyArchives });
    }

    if (!pushes.length) {
      this.markSeeded(userId);
      done();
      return;
    }

    this._loggerService.debug('Seeding ' + pushes.length + ' legacy local chat list(s) to the empty server.');
    let remaining = pushes.length;
    let allOk = true;
    const finish = (ok: boolean) => {
      allOk = allOk && ok;
      remaining--;
      if (remaining === 0) {
        // Only mark seeded when the whole push succeeded, so a partial failure retries next load.
        if (allOk) {
          this.markSeeded(userId);
        }
        done();
      }
    };

    _.each(pushes, (push) => {
      this._teamnoteApiService.callApi(
        TeamNoteApiConstant.CHAT_LIST.ADD_CHAT,
        { list_id: push.listId, chat_ids: JSON.stringify(push.chatIds) },
        (resp) => {
          this.applyUpdatedList(resp);
          finish(true);
        },
        (err) => {
          this._loggerService.error('Legacy chat list seeding failed for list ' + push.listId
            + '. status=' + (err && err.status));
          finish(false);
        },
        false,
        false,
        true
      );
    });
  }

  // ----- Local persistence (per-user) -----

  private persistLocal(): void {
    const userId = this._accountManagerService.userId;
    if (!userId) {
      return;
    }
    try {
      this._localStorageManagerService.setLocalStorageByKey(
        ChatListConstant.LOCAL_STORAGE.LISTS,
        JSON.stringify({ user_id: userId, lists: this.lists })
      );
    } catch (e) {
      this._loggerService.error('Failed to persist chat lists locally: ' + e);
    }
  }

  private hydrateFromLocal(): void {
    const userId = this._accountManagerService.userId;
    if (!userId) {
      return;
    }
    const raw = this._localStorageManagerService.getLocalStorageByKey(ChatListConstant.LOCAL_STORAGE.LISTS);
    if (!raw) {
      return;
    }
    let parsed;
    try {
      parsed = JSON.parse(raw);
    } catch (e) {
      this._loggerService.error('Failed to parse persisted chat lists: ' + e);
      return;
    }
    if (!parsed || parsed.user_id !== userId || !parsed.lists) {
      return;
    }
    this.lists = parsed.lists;
    this.resolveSystemListIds();
    this.rebuildMembershipIndex();
  }

  // ----- helpers -----

  private readLegacyChatIds(key: string): string[] {
    const raw = this._localStorageManagerService.getLocalStorageByKey(key);
    if (!raw) {
      return [];
    }
    return _.compact(('' + raw).split(','));
  }

  private isListEmpty(listId: string): boolean {
    const list = this.lists[listId];
    return !list || !list.chats || list.chats.length === 0;
  }

  private markSeeded(userId: string): void {
    this._localStorageManagerService.setLocalStorageByKey(
      ChatListConstant.LOCAL_STORAGE.MIGRATED_USER_ID, userId
    );
  }

  private parseUpdatedAt(updatedAt: string): number {
    const parsed = parseFloat(updatedAt);
    return isNaN(parsed) ? 0 : parsed;
  }
}
